import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CLoadingButton,
  CSpinner,
} from '@coreui/react-pro'
import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import TeamAlertService from 'src/service/TeamAlertService'
import ToastComponent from 'src/components/common/TaostComponent'
import useSponsorQuillModules from '../Sponsor/useSponsorQuillModules'

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required').max(50, '50 Character Limit is allowed.'),
  short_description: Yup.string()
    .required('Short description is required')
    .max(150, '150 Character Limit is allowed.'),
})

const EditForm = ({ alertId, visible, onClose, fetchUsers }) => {
  const [alertDetail, setAlertDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const quillRef = useRef()
  const modules = useSponsorQuillModules(quillRef)

  useEffect(() => {
    if (visible && alertId) {
      setDetailLoading(true)
      TeamAlertService.teamAlertDetail(alertId)
        .then((res) => {
          if (res.status === 200) {
            setAlertDetail(res.data)
          }
        })
        .catch((e) => {
          ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
        })
        .finally(() => setDetailLoading(false))
    }
  }, [visible, alertId])

  const formik = useFormik({
    initialValues: {
      title: alertDetail?.title || '',
      short_description: alertDetail?.short_description || '',
      description: alertDetail?.description || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      const formData = new FormData()
      formData.append('articleId', alertId)
      formData.append('title', data.title)
      formData.append('short_description', data.short_description)
      formData.append('description', data.description)

      setLoader(true)
      TeamAlertService.editTeamAlert(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            onClose()
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  const handleClose = () => {
    setErrorMessages([])
    setAlertDetail(null)
    formik.resetForm()
    onClose()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 560 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Alert</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Update {alertDetail?.title || 'this alert'}&apos;s title and description.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        {alertDetail?.is_sent === 1 && (
          <CAlert color="warning">
            This alert has already been sent to members. Changes here only update the record - it
            won&apos;t be re-sent.
          </CAlert>
        )}
        {detailLoading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading alert details...</span>
          </div>
        ) : (
          <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
            <CCol xs={12}>
              <CFormLabel htmlFor="title">Title *</CFormLabel>
              <CFormInput
                id="title"
                name="title"
                placeholder="Title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.errors.title && formik.touched.title)}
              />
              {formik.errors.title && formik.touched.title && (
                <CFormFeedback invalid>{formik.errors.title}</CFormFeedback>
              )}
            </CCol>
            <CCol xs={12}>
              <CFormLabel htmlFor="short_description">Short Description (For App) *</CFormLabel>
              <CFormTextarea
                id="short_description"
                name="short_description"
                rows={2}
                value={formik.values.short_description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.errors.short_description && formik.touched.short_description)}
              />
              {formik.errors.short_description && formik.touched.short_description && (
                <CFormFeedback invalid>{formik.errors.short_description}</CFormFeedback>
              )}
            </CCol>
            <CCol xs={12}>
              <CFormLabel htmlFor="description">Long Description (For Web)</CFormLabel>
              <div className="ta-editor">
                <ReactQuill
                  theme="snow"
                  ref={quillRef}
                  value={formik.values.description}
                  onChange={(content) => formik.setFieldValue('description', content)}
                  modules={modules}
                />
              </div>
            </CCol>
          </CForm>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
          Save Changes
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default EditForm
